import { Menu, LogOut, Moon, Sun } from 'lucide-react'
import { useAppDispatch, useAppSelector } from '@/app/hooks'
import { toggleSidebar, setTheme } from '@/features/ui/ui-slice'
import { logout } from '@/features/auth/auth-slice'
import { useLogoutMutation } from '@/features/auth/auth-api'
import { api } from '@/app/api'
import { Button } from '@/components/ui/button'
import { useNavigate } from 'react-router-dom'
import { NotificationBell } from '@/components/shared/notification-bell'
import { LocaleSwitcher } from '@/components/shared/locale-switcher'
import { useI18n } from '@/lib/i18n'

export function Topbar() {
  const dispatch = useAppDispatch()
  const navigate = useNavigate()
  const user = useAppSelector((s) => s.auth.user)
  const company = useAppSelector((s) => s.auth.company)
  const theme = useAppSelector((s) => s.ui.theme)
  const [logoutMutation] = useLogoutMutation()
  const { t } = useI18n()

  async function handleLogout() {
    try {
      await logoutMutation().unwrap()
    } catch {
      // ignore — clear locally regardless
    }
    // Navigate to the public login route FIRST, so the protected route tree
    // unmounts and RequireAuth can't capture the current page as `from`
    // (otherwise the next user lands on the previous user's page → 403).
    navigate('/login', { replace: true })
    dispatch(logout())
    // Wipe the RTK Query cache so the next user never inherits stale data,
    // permissions, or modules from the previous session.
    dispatch(api.util.resetApiState())
  }

  function toggleTheme() {
    const next = theme === 'light' ? 'dark' : 'light'
    dispatch(setTheme(next))
    document.documentElement.classList.toggle('dark', next === 'dark')
  }

  return (
    <header className="flex h-16 items-center justify-between border-b border-border bg-card px-4">
      <div className="flex items-center gap-3">
        <Button variant="ghost" size="icon" onClick={() => dispatch(toggleSidebar())}>
          <Menu className="h-5 w-5" />
        </Button>
        {company && (
          <div className="hidden sm:block">
            <p className="text-sm font-semibold">{company.name}</p>
            <p className="font-mono text-[10px] uppercase tracking-wider text-muted-foreground">
              {company.industry_template_code.replace(/_/g, ' ')}
            </p>
          </div>
        )}
      </div>

      <div className="flex items-center gap-2">
        <LocaleSwitcher />
        <NotificationBell />
        <Button variant="ghost" size="icon" onClick={toggleTheme}>
          {theme === 'light' ? <Moon className="h-5 w-5" /> : <Sun className="h-5 w-5" />}
        </Button>

        <div className="flex items-center gap-3 border-l border-border pl-3">
          <div className="hidden text-right sm:block">
            <p className="text-sm font-medium">{user?.full_name}</p>
            <p className="text-xs text-muted-foreground">{user?.email}</p>
          </div>
          <div className="flex h-9 w-9 items-center justify-center rounded-full bg-accent/15 font-semibold text-accent">
            {user?.full_name?.charAt(0).toUpperCase() ?? '?'}
          </div>
          <Button variant="ghost" size="icon" onClick={handleLogout} title={t('auth.sign_out')}>
            <LogOut className="h-5 w-5" />
          </Button>
        </div>
      </div>
    </header>
  )
}
